import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, HeartHandshake } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { EmptyState, fmtTime } from "@/components/PageHeader";
import ConfirmDialog from "@/components/ConfirmDialog";
import VolunteerSheetDialog from "@/components/VolunteerSheetDialog";
import { prettyYmd } from "@/components/ConflictDialog";
import { CoachSwapRequests } from "@/components/VolunteerSwaps";

export function FillBar({ filled, spots, testId }) {
  const pct = spots ? Math.min(100, Math.round((filled / spots) * 100)) : 0;
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${filled >= spots ? "bg-emerald-500" : "bg-teal-500"}`} style={{ width: `${pct}%` }} /></div>
      <span className="text-xs font-semibold tabular-nums text-slate-600">{filled} of {spots} filled</span>
    </div>
  );
}

export const blockTime = (b) => `${fmtTime(b.start_time)} – ${fmtTime(b.end_time)}${b.label ? ` · ${b.label}` : ""}`;

function CoachBlock({ b, roster, onAdd, onRemove }) {
  const open = b.spots - b.filled;
  const taken = new Set(b.signups.map((s) => s.member_id));
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3" data-testid={`vol-block-${b.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-800">{blockTime(b)}</p>
        <div className="flex items-center gap-2">
          <FillBar filled={b.filled} spots={b.spots} testId={`vol-block-fill-${b.id}`} />
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${open > 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`vol-block-status-${b.id}`}>{open > 0 ? `${open} open` : "Filled"}</span>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {b.signups.map((s) => (
          <span key={s.id} className="flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200" data-testid={`vol-signup-${s.id}`}>
            {s.member_name}{s.added_by_coach && <span className="text-[10px] text-slate-400">(added)</span>}
            {s.swapped_from_name && <span className="text-[10px] text-teal-600" data-testid={`vol-signup-swapped-${s.id}`}>(swap from {s.swapped_from_name})</span>}
            <button type="button" className="text-slate-400 hover:text-red-500" onClick={() => onRemove(s)} data-testid={`vol-signup-remove-${s.id}`} aria-label={`Remove ${s.member_name}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
        {open > 0 && (
          <Select value="" onValueChange={(mid) => onAdd(b, mid)}>
            <SelectTrigger className="h-7 w-44 rounded-full text-xs" data-testid={`vol-block-add-${b.id}`}><SelectValue placeholder="+ Add member" /></SelectTrigger>
            <SelectContent>
              {roster.filter((m) => !taken.has(m.id)).map((m) => <SelectItem key={m.id} value={m.id} data-testid={`vol-add-option-${m.id}`}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}

function SheetCard({ sheet, roster, onEdit, onDelete, onAdd, onRemove }) {
  const blocks = sheet.events.flatMap((e) => e.blocks);
  const filled = blocks.reduce((n, b) => n + b.filled, 0);
  const spots = blocks.reduce((n, b) => n + b.spots, 0);
  return (
    <Card className="border-slate-200 p-5 shadow-sm" data-testid={`vol-sheet-${sheet.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-heading text-lg font-semibold text-slate-900">{sheet.title}</p>
          {sheet.details && <p className="text-sm text-slate-500">{sheet.details}</p>}
          <p className="mt-1 text-xs text-slate-400">{sheet.events.length} date{sheet.events.length === 1 ? "" : "s"} · {filled} of {spots} spots filled</p>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => onEdit(sheet)} data-testid={`vol-sheet-edit-${sheet.id}`}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => onDelete(sheet)} data-testid={`vol-sheet-delete-${sheet.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="mt-4 space-y-4">
        {sheet.events.map((ev) => (
          <div key={ev.id} data-testid={`vol-sheet-event-${ev.id}`}>
            <p className="text-sm font-bold text-slate-800">{prettyYmd(ev.event_date)}</p>
            <p className="mb-2 text-sm text-slate-500">{ev.description}</p>
            <div className="space-y-2">{ev.blocks.map((b) => <CoachBlock key={b.id} b={b} roster={roster} onAdd={onAdd} onRemove={onRemove} />)}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function CoachVolunteers() {
  const [sheets, setSheets] = useState([]);
  const [today, setToday] = useState("");
  const [roster, setRoster] = useState([]);
  const [view, setView] = useState("upcoming");
  const [dialog, setDialog] = useState(null);
  const [del, setDel] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [swaps, setSwaps] = useState(null);

  const load = useCallback(() => {
    api.get("/volunteers/swaps").then(({ data }) => setSwaps(data)).catch(() => {});
    return api.get("/volunteers/sheets").then(({ data }) => { setSheets(data.items || []); setToday(data.today); }).catch(() => {});
  }, []);
  useEffect(() => { load(); api.get("/volunteers/roster").then(({ data }) => setRoster(data.items || [])).catch(() => {}); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const add = (b, mid) => act(() => api.post(`/volunteers/blocks/${b.id}/signups`, { member_id: mid }), "Volunteer added");
  const shown = sheets.filter((s) => (view === "past") === (s.events[s.events.length - 1].event_date < today));

  return (
    <div data-testid="coach-volunteers-page">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Post volunteer sign-ups and see who's covering each time block.</p>
        <Button className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => setDialog({})} data-testid="vol-new-sheet-btn"><Plus className="mr-2 h-4 w-4" /> New sign-up sheet</Button>
      </div>
      <CoachSwapRequests data={swaps} onDone={load} />
      <div className="mb-4 flex gap-2">
        {["upcoming", "past"].map((v) => (
          <Button key={v} size="sm" variant={view === v ? "default" : "outline"} className={`rounded-full capitalize ${view === v ? "bg-slate-900" : ""}`} onClick={() => setView(v)} data-testid={`vol-view-${v}`}>{v}</Button>
        ))}
      </div>
      {shown.length === 0 ? (
        <EmptyState icon={HeartHandshake} title={view === "past" ? "No past sign-ups" : "No upcoming sign-ups"} description="Create a sign-up sheet to start recruiting volunteers." testId="vol-empty" />
      ) : (
        <div className="grid max-w-4xl gap-4">
          {shown.map((s) => <SheetCard key={s.id} sheet={s} roster={roster} onEdit={(x) => setDialog({ editing: x })} onDelete={setDel} onAdd={add} onRemove={setRemoving} />)}
        </div>
      )}
      <VolunteerSheetDialog open={!!dialog} editing={dialog?.editing} onClose={() => setDialog(null)} onSaved={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete sign-up sheet?" destructive confirmText="Delete" testId="vol-delete-dialog"
        description={del ? `"${del.title}" and all of its sign-ups will be removed.` : ""}
        onConfirm={() => { const s = del; setDel(null); act(() => api.delete(`/volunteers/sheets/${s.id}`), "Sign-up sheet deleted"); }} />
      <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} title="Remove volunteer?" destructive confirmText="Remove" testId="vol-remove-dialog"
        description={removing ? `${removing.member_name} will be removed from ${removing.block_text} and notified.` : ""}
        onConfirm={() => { const s = removing; setRemoving(null); act(() => api.delete(`/volunteers/signups/${s.id}`), "Volunteer removed"); }} />
    </div>
  );
}
